import { useEffect, useState, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { Link } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { obtenerSetupIntent } from '../../../actions/membresia';
import { obtenerMetodoPago, actualizarMetodoPago, eliminarMetodoPago } from '../../../actions/configuracion';

import { formatNumber } from '../../../utils/generalFunctions';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const cardElementOptions = {
    style: {
        base: {
            fontSize: '16px',
            color: '#212529',
            fontFamily: 'inherit',
            '::placeholder': { color: '#6c757d' }
        },
        invalid: { color: '#dc3545' }
    },
    hidePostalCode: true
};

const handleTarjetaIcono = (marca, size = '2x') => {
    switch(marca?.toLowerCase()){
        case 'visa':       return <i className={`fa-brands fa-cc-visa fa-${size}`}></i>;
        case 'mastercard': return <i className={`fa-brands fa-cc-mastercard fa-${size}`}></i>;
        case 'amex':       return <i className={`fa-brands fa-cc-amex fa-${size}`}></i>;
        default:           return <i className={`fa-solid fa-credit-card fa-${size}`}></i>;
    }
};

/* Formulario interno — debe estar dentro de <Elements> */
const FormMetodoPago = ({ clientSecret, onGuardar, statusProcesar, stripeError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [procesando, setProcesando] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if(!stripe || !elements || !clientSecret || procesando) return;

        setProcesando(true);
        const cardElement = elements.getElement(CardElement);
        const { setupIntent, error } = await stripe.confirmCardSetup(clientSecret, {
            payment_method: { card: cardElement }
        });
        setProcesando(false);

        if(error){
            onGuardar(null, error.message);
            return;
        }

        onGuardar(setupIntent.payment_method);
    };

    const ocupado = procesando || statusProcesar;

    return (
        <form onSubmit={handleSubmit}>
            <div className="mb-3">
                <label className="fw-semibold mb-2 d-block text-dark">Número de tarjeta</label>
                <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                    <CardElement options={cardElementOptions} />
                </div>
                {stripeError && (
                    <small className="text-danger mt-1 d-block">
                        <i className="fa-solid fa-circle-exclamation me-1"></i>{stripeError}
                    </small>
                )}
            </div>

            <div className="d-flex justify-content-end mt-3">
                {ocupado
                    ? <button type="button" className="btn btn-success" disabled>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Guardando...
                      </button>
                    : <button type="submit" className="btn btn-success" disabled={!clientSecret}>
                        <i className="fa-solid fa-floppy-disk me-2"></i>Guardar tarjeta
                      </button>
                }
            </div>
        </form>
    );
};

/* Vista principal */
const MetodoPago = () => {

    let rutasBread = [
        {
            activo: true,
            nombre: 'Método de pago',
            path: '/configuracion/metodo_pago'
        }
    ];

    const dispatch = useDispatch();

    const { setup_intent } = useSelector(state => state.membresia);
    const { metodo_pago } = useSelector(state => state.configuracion);
    const { usuario, membresia_actual } = useSelector(state => state.auth);

    const [statusProcesar, setStatusProcesar] = useState(false);
    const [stripeError, setStripeError] = useState(null);
    const [mostrarFormulario, setMostrarFormulario] = useState(false);
    const [pendingPaymentMethodId, setPendingPaymentMethodId] = useState(null);
    const [confirmarEliminar, setConfirmarEliminar] = useState(false);

    const hayTarjeta = !!metodo_pago?.digitos;
    const formVisible = mostrarFormulario || !hayTarjeta;

    const nombreTitular = [usuario?.nombre, usuario?.apellido_paterno, usuario?.apellido_materno]
        .filter(Boolean).join(' ').toUpperCase() || 'TITULAR';

    const handleGuardar = async (payment_method_id, error = null) => {
        if(error){
            setStripeError(error);
            return;
        }
        setStripeError(null);

        if(hayTarjeta){
            setPendingPaymentMethodId(payment_method_id);
            return;
        }

        await confirmarGuardar(payment_method_id);
    };

    const confirmarGuardar = async (payment_method_id) => {
        setStatusProcesar(true);
        await dispatch(actualizarMetodoPago(payment_method_id));
        setStatusProcesar(false);
        setPendingPaymentMethodId(null);
        setMostrarFormulario(false);
    };

    const cancelarConfirmacion = () => {
        setPendingPaymentMethodId(null);
    };

    const handleEliminar = async () => {
        setStatusProcesar(true);
        await dispatch(eliminarMetodoPago());
        setStatusProcesar(false);
        setConfirmarEliminar(false);
    };

    useEffect(() => {
        dispatch(obtenerMetodoPago());
        dispatch(obtenerSetupIntent());
    }, []);

    useEffect(() => {
        dispatch(editarBreadcrumb(rutasBread));
    }, [rutasBread]);

    return (
        <Fragment>
            <HelmetProvider>
                <Helmet>
                    <style>{`
                        .tarjeta-visual {
                            background: linear-gradient(135deg, #111F62 0%, #21B4E9 100%);
                            border-radius: 16px;
                            color: #fff;
                            padding: 28px 28px 24px;
                            width: 340px;
                            max-width: 100%;
                            aspect-ratio: 1.586;
                            position: relative;
                            box-shadow: 0 8px 32px rgba(17,31,98,0.35);
                            display: flex;
                            flex-direction: column;
                            justify-content: space-between;
                            user-select: none;
                        }
                        .tarjeta-chip {
                            width: 42px;
                            height: 32px;
                            background: rgba(255,255,255,0.3);
                            border-radius: 6px;
                            border: 1px solid rgba(255,255,255,0.5);
                        }
                        .tarjeta-numero {
                            font-size: 1.25rem;
                            letter-spacing: 4px;
                            font-weight: 600;
                            margin: 0;
                            color: #fff;
                        }
                        .tarjeta-label {
                            font-size: 0.65rem;
                            color: rgba(255,255,255,0.75);
                            text-transform: uppercase;
                            letter-spacing: 1px;
                            display: block;
                            margin-bottom: 3px;
                        }
                        .security-badge {
                            background: #edf7f4;
                            border: 1px solid #b2dfdb;
                            border-left: 4px solid #0CB394;
                            border-radius: 8px;
                            padding: 16px 20px;
                        }
                        .security-badge li span,
                        .security-badge p {
                            color: #212529;
                        }
                        .membresia-info-card {
                            background: #eef1fb;
                            border: 1px solid #c5cae9;
                            border-left: 4px solid #2F328A;
                            border-radius: 8px;
                            padding: 16px 20px;
                        }
                        .membresia-info-card p {
                            color: #212529;
                        }
                        .faq-accordion .accordion-button {
                            color: #212529 !important;
                            background: transparent !important;
                            box-shadow: none !important;
                            font-weight: 600;
                            font-size: 0.875rem;
                            padding-left: 0;
                            padding-right: 0;
                        }
                        .faq-accordion .accordion-button:not(.collapsed) {
                            color: #2F328A !important;
                        }
                        .faq-accordion .accordion-body {
                            padding-left: 0;
                            padding-right: 0;
                            color: #495057;
                            font-size: 0.85rem;
                        }
                        .faq-accordion .accordion-item {
                            border: none;
                            border-bottom: 1px solid #dee2e6;
                            background: transparent;
                        }
                    `}</style>
                </Helmet>
            </HelmetProvider>

            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="title-section my-5">Método de pago</h3>

                    <div className="row g-5">

                        {/* COLUMNA IZQUIERDA — tarjeta y formulario */}
                        <div className="col-md-7">

                            {/* Tarjeta visual */}
                            <h5 className="subtitle-section mb-3">Tarjeta registrada</h5>

                            {hayTarjeta ? (
                                <Fragment>
                                    <div className="tarjeta-visual mb-4">
                                        <div className="d-flex justify-content-between align-items-flex-start">
                                            <div className="tarjeta-chip"></div>
                                            <div style={{ opacity: 0.9 }}>
                                                {handleTarjetaIcono(metodo_pago.marca, '2x')}
                                            </div>
                                        </div>

                                        <p className="tarjeta-numero">
                                            •••• &nbsp;•••• &nbsp;•••• &nbsp;{metodo_pago.digitos}
                                        </p>

                                        <div className="d-flex justify-content-between align-items-flex-end">
                                            <div>
                                                <span className="tarjeta-label">Titular</span>
                                                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{nombreTitular}</span>
                                            </div>
                                            <div>
                                                <span className="tarjeta-label">Vence</span>
                                                <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                                    {String(metodo_pago.expiracion_mes).padStart(2,'0')} / {String(metodo_pago.expiracion_ano).slice(-2)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Botones de acción */}
                                    {!pendingPaymentMethodId && !confirmarEliminar && (
                                        <div className="d-flex gap-2 mb-4">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${mostrarFormulario ? 'btn-outline-secondary' : 'btn-outline-primary'}`}
                                                onClick={() => { setMostrarFormulario(v => !v); setStripeError(null); }}
                                            >
                                                <i className={`fa-solid ${mostrarFormulario ? 'fa-xmark' : 'fa-rotate'} me-2`}></i>
                                                {mostrarFormulario ? 'Cancelar cambio' : 'Cambiar tarjeta'}
                                            </button>
                                            {!mostrarFormulario && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => setConfirmarEliminar(true)}
                                                >
                                                    <i className="fa-solid fa-trash me-2"></i>Eliminar tarjeta
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Confirmación eliminar */}
                                    {confirmarEliminar && (
                                        <div className="alert alert-danger mb-4" role="alert">
                                            <h6 className="alert-heading mb-1">
                                                <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                                ¿Eliminar tarjeta?
                                            </h6>
                                            <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                                                Se eliminará la tarjeta terminada en <strong>{metodo_pago?.digitos}</strong>. No podrás realizar renovaciones hasta agregar una nueva.
                                            </p>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-danger btn-sm"
                                                    onClick={handleEliminar}
                                                    disabled={statusProcesar}
                                                >
                                                    {statusProcesar
                                                        ? <><span className="spinner-border spinner-border-sm me-1"></span>Eliminando...</>
                                                        : <><i className="fa-solid fa-trash me-1"></i>Sí, eliminar</>
                                                    }
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary btn-sm"
                                                    onClick={() => setConfirmarEliminar(false)}
                                                    disabled={statusProcesar}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </Fragment>
                            ) : (
                                <p className="text-muted mb-4">
                                    <i className="fa-solid fa-credit-card me-2"></i>
                                    No hay ninguna tarjeta registrada.
                                </p>
                            )}

                            {/* Confirmación antes de reemplazar */}
                            {pendingPaymentMethodId && (
                                <div className="alert alert-warning mb-4" role="alert">
                                    <h6 className="alert-heading mb-1">
                                        <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                        ¿Confirmas el cambio de tarjeta?
                                    </h6>
                                    <p className="mb-3" style={{ fontSize: '0.9rem' }}>
                                        La tarjeta actual terminada en <strong>{metodo_pago.digitos}</strong> será reemplazada. Los cobros futuros se realizarán con la nueva tarjeta.
                                    </p>
                                    <div className="d-flex gap-2">
                                        <button
                                            type="button"
                                            className="btn btn-warning btn-sm"
                                            onClick={() => confirmarGuardar(pendingPaymentMethodId)}
                                            disabled={statusProcesar}
                                        >
                                            {statusProcesar
                                                ? <><span className="spinner-border spinner-border-sm me-1"></span>Procesando...</>
                                                : <><i className="fa-solid fa-check me-1"></i>Sí, cambiar tarjeta</>
                                            }
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={cancelarConfirmacion}
                                            disabled={statusProcesar}
                                        >
                                            Cancelar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Formulario Stripe */}
                            {formVisible && !pendingPaymentMethodId && (
                                <div>
                                    <h5 className="subtitle-section mb-3">
                                        {hayTarjeta ? 'Nueva tarjeta' : 'Agregar tarjeta'}
                                    </h5>
                                    <Elements stripe={stripePromise}>
                                        <FormMetodoPago
                                            clientSecret={setup_intent}
                                            onGuardar={handleGuardar}
                                            statusProcesar={statusProcesar}
                                            stripeError={stripeError}
                                        />
                                    </Elements>
                                </div>
                            )}
                        </div>

                        {/* COLUMNA DERECHA — membresía activa + seguridad */}
                        <div className="col-md-5 d-flex flex-column gap-4">

                            {/* Membresía activa */}
                            {(membresia_actual?.status === 0 || membresia_actual?.status === 1) && (
                                <div>
                                    <h5 className="subtitle-section mb-3">Membresía activa</h5>
                                    <div className="membresia-info-card">
                                        <p className="mb-1 fw-semibold">{membresia_actual.nombre}</p>
                                        {membresia_actual.total === 0
                                            ? (
                                                <>
                                                    <p className="mb-2 text-muted" style={{ fontSize: '0.9rem' }}>Gratuita</p>
                                                    <hr className="my-2" />
                                                    <p className="mb-2 text-muted" style={{ fontSize: '0.85rem' }}>
                                                        <i className="fa-solid fa-arrow-trend-up me-1"></i>
                                                        Mejora tu plan para desbloquear más beneficios y asesorías.
                                                    </p>
                                                    <Link className="btn btn-sm btn-success w-100" to="/membresia">
                                                        Ver planes disponibles
                                                    </Link>
                                                </>
                                            )
                                            : <p className="mb-1 text-muted" style={{ fontSize: '0.9rem' }}>
                                                {formatNumber(membresia_actual.total, 2, '$ ', '')} / año · IVA inc.
                                              </p>
                                        }
                                        {membresia_actual.fecha_renovacion && (
                                            <p className="mb-0 text-muted" style={{ fontSize: '0.85rem' }}>
                                                <i className="fa-solid fa-calendar-check me-1"></i>
                                                Próximo cobro:{' '}
                                                {new Date(membresia_actual.fecha_renovacion).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Seguridad */}
                            <div>
                                <h5 className="subtitle-section mb-3">Seguridad</h5>
                                <div className="security-badge">
                                    <div className="d-flex align-items-center gap-2 mb-3">
                                        <i className="fa-brands fa-stripe fa-2x" style={{ color: '#635BFF' }}></i>
                                        <span className="fw-semibold" style={{ fontSize: '0.9rem' }}>Pagos procesados por Stripe</span>
                                    </div>
                                    <ul className="list-unstyled mb-0" style={{ fontSize: '0.85rem' }}>
                                        <li className="mb-2 d-flex align-items-start gap-2">
                                            <i className="fa-solid fa-lock text-success mt-1" style={{ fontSize: '0.75rem', flexShrink: 0 }}></i>
                                            <span>Goodlic <strong>nunca almacena</strong> los datos de tu tarjeta.</span>
                                        </li>
                                        <li className="mb-2 d-flex align-items-start gap-2">
                                            <i className="fa-solid fa-shield-halved text-success mt-1" style={{ fontSize: '0.75rem', flexShrink: 0 }}></i>
                                            <span>Tu información viaja cifrada con <strong>TLS / SSL</strong>.</span>
                                        </li>
                                        <li className="mb-2 d-flex align-items-start gap-2">
                                            <i className="fa-solid fa-certificate text-success mt-1" style={{ fontSize: '0.75rem', flexShrink: 0 }}></i>
                                            <span>Stripe cumple con el estándar <strong>PCI DSS nivel 1</strong>.</span>
                                        </li>
                                        <li className="d-flex align-items-start gap-2">
                                            <i className="fa-solid fa-rotate text-success mt-1" style={{ fontSize: '0.75rem', flexShrink: 0 }}></i>
                                            <span>Puedes cambiar o eliminar tu tarjeta en cualquier momento.</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            {/* FAQ */}
                            <div>
                                <h5 className="subtitle-section mb-3">Preguntas frecuentes</h5>
                                <div className="accordion accordion-flush faq-accordion" id="accordionFAQ">
                                    {[
                                        {
                                            id: 'faq1',
                                            pregunta: '¿Cuándo se realiza el cobro?',
                                            respuesta: 'El cobro se realiza automáticamente cada año en la fecha de renovación de tu membresía.'
                                        },
                                        {
                                            id: 'faq2',
                                            pregunta: '¿Qué pasa si falla el cobro?',
                                            respuesta: 'Si el cargo no puede procesarse, tu membresía entrará en periodo de gracia. Actualiza tu tarjeta para evitar la interrupción del servicio.'
                                        },
                                        {
                                            id: 'faq3',
                                            pregunta: '¿Puedo cambiar mi tarjeta en cualquier momento?',
                                            respuesta: 'Sí. Al guardar una nueva tarjeta, todos los cobros futuros se realizarán con ella.'
                                        }
                                    ].map(({ id, pregunta, respuesta }) => (
                                        <div className="accordion-item border-0 border-bottom" key={id}>
                                            <h2 className="accordion-header">
                                                <button
                                                    className="accordion-button collapsed px-0 py-3"
                                                    type="button"
                                                    data-bs-toggle="collapse"
                                                    data-bs-target={`#${id}`}
                                                    style={{ fontSize: '0.875rem' }}
                                                >
                                                    {pregunta}
                                                </button>
                                            </h2>
                                            <div id={id} className="accordion-collapse collapse" data-bs-parent="#accordionFAQ">
                                                <div className="accordion-body px-0 pt-0 pb-3" style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                                    {respuesta}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

export default MetodoPago;
